<template>
  <div id="client-notes" class="cn-root">
    <div v-if="loaded">

      <!-- Header -->
      <div class="cn-header">
        <div class="cn-title">
          <i class="fa-solid fa-sticky-note cn-title-icon"></i>
          <span>{{ $t('tabs.clients.notes') }}</span>
          <span class="cn-count" v-if="notes.length">{{ filtered.length }}/{{ notes.length }}</span>
        </div>
        <div class="cn-header-actions">
          <b-spinner small v-if="loading"/>
          <button v-else class="cn-icon-btn" :title="$t('operations.refresh')" @click="loadData">
            <i class="fa-solid fa-rotate-right"></i>
          </button>
          <b-button size="sm" variant="primary" class="cn-add-btn" @click="openModal()">
            <i class="fa-solid fa-plus mr-1"></i>{{ $t('operations.addNewNote') }}
          </b-button>
        </div>
      </div>

      <!-- Toolbar: search + filter chips -->
      <div class="cn-toolbar">
        <b-input-group size="sm" class="cn-search">
          <b-input-group-prepend>
            <b-input-group-text><i class="fa-solid fa-magnifying-glass"></i></b-input-group-text>
          </b-input-group-prepend>
          <b-form-input v-model="search" :placeholder="$t('operations.search')"/>
        </b-input-group>
        <div class="cn-chips">
          <button class="cn-chip" :class="{ active: filter === 'all' }" @click="filter = 'all'">
            {{ $t('all') }}
          </button>
          <button class="cn-chip" :class="{ active: filter === 'pinned' }" @click="filter = 'pinned'">
            <i class="fa-solid fa-thumbtack"></i> {{ $t('operations.pin') }}
          </button>
          <button class="cn-chip" :class="{ active: filter === 'important' }" @click="filter = 'important'">
            <i class="fa-solid fa-star"></i> {{ $t('important') }}
          </button>
          <button class="cn-chip" :class="{ active: filter === 'mine' }" @click="filter = 'mine'">
            <i class="fa-solid fa-user"></i> {{ $t('mine') }}
          </button>
        </div>
      </div>

      <!-- List -->
      <div class="cn-list" v-if="filtered.length > 0">
        <div v-for="note in filtered" :key="note.id"
             class="cn-note"
             :class="{ pinned: note.isPinned, important: note.isImportant, 'cn-note-mine': note.userId === myId }"
        >
          <div class="cn-note-header">
            <div class="cn-note-meta">
              <span class="cn-author-chip" :class="{ 'cn-author-chip-mine': note.userId === myId }">
                <span class="cn-author-avatar">{{ authorInitials(note) }}</span>
                <span class="cn-author-name">{{ authorName(note) }}</span>
                <span v-if="note.userId === myId" class="cn-author-you">{{ $t('byYou') }}</span>
              </span>
              <span class="cn-time">
                <i class="fa-regular fa-clock"></i>
                {{ note.time | moment($t('timeFormat.dateTime.format')) }}
              </span>
              <span v-if="note.isPublic" class="cn-flag cn-flag-public" :title="$t('public')">
                <i class="fas fa-lock-open"></i> {{ $t('public') }}
              </span>
              <span v-else class="cn-flag cn-flag-private" :title="$t('private')">
                <i class="fas fa-lock"></i> {{ $t('private') }}
              </span>
            </div>
            <div class="cn-note-actions">
              <button class="cn-icon-btn" :class="{ active: note.isPinned }"
                      :title="note.isPinned ? $t('operations.unpin') : $t('operations.pin')"
                      @click="togglePin(note)">
                <i class="fa-solid fa-thumbtack"></i>
              </button>
              <button class="cn-icon-btn" :title="$t('operations.edit')" @click="loadNote(note.id)">
                <i class="fa fa-edit"></i>
              </button>
              <button v-if="canDeleteNote(note)" class="cn-icon-btn cn-icon-danger"
                      :title="$t('operations.delete')" @click="onDeleteNote(note.id)">
                <i class="fa fa-trash"></i>
              </button>
            </div>
          </div>
          <div class="cn-note-body">{{ note.text }}</div>
          <div class="cn-note-tags" v-if="note.isPinned || note.isImportant">
            <span v-if="note.isPinned" class="cn-tag cn-tag-pinned">
              <i class="fa-solid fa-thumbtack"></i> {{ $t('operations.pin') }}
            </span>
            <span v-if="note.isImportant" class="cn-tag cn-tag-important">
              <i class="fa-solid fa-star"></i> {{ $t('important') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <div v-else class="cn-empty">
        <i class="fa-regular fa-note-sticky cn-empty-icon"></i>
        <div class="cn-empty-text">
          {{ search || filter !== 'all' ? $t('noResults') : $t('noNotesYet') }}
        </div>
        <b-button v-if="!search && filter === 'all'" size="sm" variant="outline-primary" class="mt-2" @click="openModal()">
          <i class="fa-solid fa-plus mr-1"></i>{{ $t('operations.addNewNote') }}
        </b-button>
      </div>

      <!-- Editor modal -->
      <b-modal v-model="showModal" no-close-on-backdrop hide-footer hide-header centered
               dialog-class="cn-modal-dialog" body-class="cn-modal-body">
        <div class="cn-modal-header">
          <div class="cn-modal-icon"><i class="fa-solid fa-sticky-note"></i></div>
          <div class="cn-modal-titles">
            <div class="cn-modal-title">{{ newNote.id ? $t('operations.editNote') : $t('operations.newNote') }}</div>
            <div class="cn-modal-subtitle">{{ $t('note') }}</div>
          </div>
          <button class="cn-modal-close" @click="showModal = false" :title="$t('operations.close') || 'Close'">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>

        <b-form @submit.prevent="save" class="cn-modal-form">
          <b-form-textarea v-model="newNote.text" rows="6" autofocus
                           class="cn-modal-textarea"
                           :placeholder="$t('writeNoteHere')"/>

          <div class="cn-modal-flags-row">
            <button type="button" class="cn-flag-toggle" :class="{ on: newNote.isPublic }"
                    @click="newNote.isPublic = !newNote.isPublic">
              <i :class="['fas', newNote.isPublic ? 'fa-lock-open' : 'fa-lock']"></i>
              <span>{{ newNote.isPublic ? $t('public') : $t('private') }}</span>
            </button>
            <button type="button" class="cn-flag-toggle cn-flag-important" :class="{ on: newNote.isImportant }"
                    @click="newNote.isImportant = !newNote.isImportant">
              <i class="fa-solid fa-star"></i>
              <span>{{ $t('important') }}</span>
            </button>
            <button type="button" class="cn-flag-toggle cn-flag-pin" :class="{ on: newNote.isPinned }"
                    @click="newNote.isPinned = !newNote.isPinned">
              <i class="fa-solid fa-thumbtack"></i>
              <span>{{ $t('operations.pin') }}</span>
            </button>
          </div>

          <div class="cn-modal-footer">
            <b-button variant="link" class="cn-modal-cancel" @click="showModal = false">
              {{ $t('operations.cancel') }}
            </b-button>
            <b-button type="submit" variant="primary" class="cn-modal-submit" :disabled="!newNote.text">
              <i class="fa-solid fa-floppy-disk mr-1"></i>{{ $t('operations.save') }}
            </b-button>
          </div>
        </b-form>
      </b-modal>

    </div>

    <div v-else-if="error !== ''">
      <b-alert show variant="danger">
        <i class="fas fa-exclamation-triangle mr-2"></i><strong>{{ error }}</strong>
      </b-alert>
    </div>

    <div v-else class="cn-loading">
      <b-spinner label="Spinning"/>
      <div>{{ $t('operations.loading') }}</div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import Permissions from '@/model/Permissions';

export default {
  name: 'ClientNotes',
  mixins: [mixinBase, mixinPermissions, mixinNotifications],
  props: {
    autoload: {
      default: false,
      type: Boolean
    },
    clientId: String,
  },
  data() {
    return {
      loaded: false,
      loading: false,
      error: '',
      showModal: false,
      newNote: this.defaultNewNote(),
      notes: [],
      search: '',
      filter: 'all',
    };
  },
  watch: {
    clientId() {
      this.reset();
      if (this.autoload) {
        this.loadData();
      }
    }
  },
  created() {
    this.loadData();
  },
  computed: {
    ...mapGetters('data', ['getBusinessUnitNameById', 'getOrganizationNameById', 'getUserFullName', 'getAffiliateNameById']),
    ...mapGetters('user', ['myId']),
    notesSorted() {
      return [...this.notes].sort((a, b) => {
        const pinDiff = (b.isPinned === true) - (a.isPinned === true);
        if (pinDiff !== 0) return pinDiff;
        const impDiff = (b.isImportant === true) - (a.isImportant === true);
        if (impDiff !== 0) return impDiff;
        const ta = a.time ? new Date(a.time).getTime() : 0;
        const tb = b.time ? new Date(b.time).getTime() : 0;
        return tb - ta;
      });
    },
    filtered() {
      const q = (this.search || '').trim().toLowerCase();
      return this.notesSorted.filter(n => {
        if (this.filter === 'pinned' && !n.isPinned) return false;
        if (this.filter === 'important' && !n.isImportant) return false;
        if (this.filter === 'mine' && n.userId !== this.myId) return false;
        if (q) {
          const text = (n.text || '').toLowerCase();
          const author = (this.authorName(n) || '').toLowerCase();
          if (!text.includes(q) && !author.includes(q)) return false;
        }
        return true;
      });
    }
  },
  methods: {
    ...mapActions('notes', ['getNotes', 'get', 'update', 'register', 'deleteNote']),

    togglePin(note) {
      const updated = { ...note, isPinned: !note.isPinned };
      this.update({ id: updated.id, note: updated })
          .then(() => {
            this.loadData();
          });
    },
    defaultNewNote() {
      return {
        clientId: this.clientId,
        text: '',
        isPublic: true,
        isImportant: false,
        isPinned: false,
      };
    },
    openModal() {
      this.newNote = this.defaultNewNote();
      this.showModal = true;
    },
    reset() {
      this.loaded = false;
      this.error = '';
      this.newNote = this.defaultNewNote();
      this.notes = [];
    },
    loadData() {
      if (this.loading) return;
      this.loading = true;
      this.getNotes({ clientId: this.clientId })
          .then(noteData => {
            this.notes = noteData.data;
            this.loaded = true;
          })
          .catch(e => {
            this.error = e.toString();
          })
          .finally(() => {
            this.loading = false;
          });
    },
    loadNote(id) {
      this.get({ id })
          .then(note => {
            this.newNote = note;
            this.showModal = true;
          });
    },
    canDeleteNote(note) {
      return note.userId === this.myId || this.hasPermission(Permissions.CLIENT_NOTES_DELETE_ALL);
    },
    // Author display name: the backend fills note.userName for holders of
    // CLIENT_NOTES_USER_NAMES (it can resolve authors OUTSIDE the user's
    // visibility scope) — otherwise fall back to the local user store.
    authorName(note) {
      return note.userName || this.getUserFullName(note.userId) || '';
    },
    authorInitials(note) {
      const name = (this.authorName(note) || '').trim();
      if (!name) return '?';
      const parts = name.split(/\s+/);
      const a = parts[0] ? parts[0][0] : '';
      const b = parts.length > 1 ? parts[parts.length - 1][0] : '';
      return (a + b).toUpperCase();
    },
    onDeleteNote(id) {
      this.deleteNote(id)
          .then(() => {
            this.loadData();
          })
          .catch(e => {
            this.showErrorNotification(this.$t('globalMessages.deleteDataError', { system: this.$t('note') }), this.getErrorDescription(e));
          });
    },
    save() {
      const action = this.newNote.id ? 'update' : 'register';
      this[action]({ note: this.newNote })
          .then(() => {
            this.reset();
            this.loadData();
            this.showModal = false;
          })
          .catch(e => {
            this.error = e.toString();
          });
    },
  }
};
</script>

<style scoped>
.cn-root {
  min-height: 220px;
  padding: 4px 2px;
}

/* ====== HEADER ====== */
.cn-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 4px 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.cn-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: #2d3142;
  text-transform: uppercase;
}

.cn-title-icon {
  color: #f8c400;
  font-size: 1.05rem;
}

.cn-count {
  font-size: 0.7rem;
  font-weight: 700;
  background: #007bff;
  color: #fff;
  padding: 2px 9px;
  border-radius: 999px;
  letter-spacing: 0.3px;
  font-variant-numeric: tabular-nums;
}

.cn-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.cn-add-btn {
  border-radius: 8px;
  font-weight: 600;
}

/* ====== TOOLBAR ====== */
.cn-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 4px 4px 14px;
  align-items: center;
  border-bottom: 1px solid #e6ebf2;
  margin-bottom: 12px;
}

.cn-search {
  flex: 1 1 220px;
  max-width: 360px;
}

.cn-search >>> .input-group-text {
  background: #f4f7fb;
  border-color: #d8dde6;
  color: #6c757d;
}

.cn-search >>> .form-control {
  border-color: #d8dde6;
}

.cn-chips {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.cn-chip {
  background: #ffffff;
  border: 1px solid #d8dde6;
  border-radius: 999px;
  padding: 5px 14px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4a5365;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.cn-chip:hover {
  background: #f0f7ff;
  color: #007bff;
  border-color: #b6d6ff;
}

.cn-chip.active {
  background: #007bff;
  color: #fff;
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.25);
}

/* ====== LIST ====== */
.cn-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 0 2px;
}

.cn-note {
  position: relative;
  background: #ffffff;
  border: 1px solid #d8dde6;
  border-left: 4px solid transparent;
  border-radius: 10px;
  padding: 14px 16px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.18s ease, border-color 0.18s ease;
}

.cn-note:hover {
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
  border-color: #b9c2d0;
}

.cn-note.pinned {
  border-left-color: #007bff;
  background: #f3f9ff;
}

.cn-note.important {
  border-left-color: #f8c400;
  background: #fffaeb;
}

.cn-note.pinned.important {
  border-left-color: #007bff;
  background: linear-gradient(120deg, #f3f9ff 0%, #fffaeb 100%);
}

/* ====== NOTE HEADER ====== */
.cn-note-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
}

.cn-note-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.78rem;
  color: #4a5365;
  flex-wrap: wrap;
}

/* Author shown as a chip with avatar — notes are user-focused, so make author prominent */
.cn-author-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #eef2f7;
  border: 1px solid #d8dde6;
  border-radius: 999px;
  padding: 2px 10px 2px 2px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #2d3142;
}

.cn-author-chip-mine {
  background: #e3f2ff;
  border-color: #b6d6ff;
}

.cn-author-avatar {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6da7ff 0%, #007bff 100%);
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.3px;
  flex-shrink: 0;
}

.cn-author-name {
  white-space: nowrap;
}

.cn-author-you {
  background: #007bff;
  color: #fff;
  font-size: 0.62rem;
  font-weight: 700;
  padding: 1px 7px;
  border-radius: 999px;
  letter-spacing: 0.3px;
  text-transform: lowercase;
  margin-left: 2px;
}

.cn-time {
  font-variant-numeric: tabular-nums;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: #6c757d;
}

.cn-flag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.cn-flag-public {
  background: #e6f7ec;
  color: #1f7a3a;
}

.cn-flag-private {
  background: #fde8ea;
  color: #b22b3a;
}

.cn-note-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.cn-icon-btn {
  background: #ffffff;
  border: 1px solid #d8dde6;
  color: #4a5365;
  width: 30px;
  height: 30px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 0.8rem;
}

.cn-icon-btn:hover {
  background: #f0f7ff;
  color: #007bff;
  border-color: #b6d6ff;
}

.cn-icon-btn.active {
  background: #007bff;
  color: #fff;
  border-color: #007bff;
}

.cn-icon-btn.cn-icon-danger:hover {
  background: #fde8ea;
  color: #dc3545;
  border-color: #f5b8be;
}

/* ====== NOTE BODY ====== */
.cn-note-body {
  font-size: 0.92rem;
  line-height: 1.5;
  color: #2d3142;
  white-space: pre-wrap;
  word-break: break-word;
}

.cn-note-tags {
  display: flex;
  gap: 6px;
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px dashed #e6ebf2;
}

.cn-tag {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 9px;
  border-radius: 999px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.cn-tag-pinned {
  background: #007bff;
  color: #fff;
}

.cn-tag-important {
  background: #f8c400;
  color: #4a3500;
}

/* ====== EMPTY ====== */
.cn-empty {
  text-align: center;
  padding: 36px 20px;
  color: #4a5365;
  background: #f4f7fb;
  border: 1px dashed #d8dde6;
  border-radius: 10px;
}

.cn-empty-icon {
  font-size: 2.6rem;
  color: #b9c2d0;
  margin-bottom: 12px;
  display: block;
}

.cn-empty-text {
  font-size: 0.95rem;
  font-weight: 500;
}

.cn-loading {
  text-align: center;
  padding: 30% 20px 20px;
}

.cn-modal-flags {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  padding: 6px 2px 4px;
}
</style>

<style>
/* Note editor modal — global because dialog-class is on the modal-dialog outside the scoped subtree */
.cn-modal-dialog .modal-content {
  border-radius: 16px;
  border: none;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.25), 0 8px 16px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}
.cn-modal-body {
  padding: 0 !important;
  background: #ffffff;
}
.dark-layout .cn-modal-body { background: #2a2f3a; }

.cn-modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 22px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-bottom: 1px solid #bfdbfe;
}
.dark-layout .cn-modal-header {
  background: linear-gradient(135deg, rgba(96, 165, 250, 0.15) 0%, rgba(96, 165, 250, 0.05) 100%);
  border-bottom-color: rgba(96, 165, 250, 0.25);
}

.cn-modal-icon {
  width: 44px; height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #60a5fa 0%, #2563eb 100%);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem;
  box-shadow: 0 3px 10px rgba(37, 99, 235, 0.35);
  flex-shrink: 0;
}

.cn-modal-titles { flex: 1; min-width: 0; }
.cn-modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.2px;
}
.cn-modal-subtitle {
  font-size: 0.74rem;
  font-weight: 600;
  color: #1d4ed8;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-top: 2px;
}
.dark-layout .cn-modal-title { color: #f1f5f9; }
.dark-layout .cn-modal-subtitle { color: #93c5fd; }

.cn-modal-close {
  width: 32px; height: 32px;
  border-radius: 8px;
  border: none;
  background: rgba(15, 23, 42, 0.05);
  color: #475569;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.15s ease;
}
.cn-modal-close:hover {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}
.dark-layout .cn-modal-close {
  background: rgba(255, 255, 255, 0.06);
  color: #cbd5e1;
}

.cn-modal-form { padding: 18px 22px 20px; }

/* Field colors mirrored from the event modal — anchored on .cn-modal-dialog
   to out-specify the Vuexy theme's global .dark-layout .form-control rules. */
.cn-modal-dialog .modal-content {
  --cnm-input-bg: #ffffff;
  --cnm-input-border: #cbd5e1;
  --cnm-input-border-strong: #94a3b8;
  --cnm-input-text: #0f172a;
  --cnm-primary: #2563eb;
  --cnm-primary-soft: #eff6ff;
}
.dark-layout .cn-modal-dialog .modal-content {
  --cnm-input-bg: rgba(255, 255, 255, 0.045);
  --cnm-input-border: rgba(255, 255, 255, 0.18);
  --cnm-input-border-strong: rgba(255, 255, 255, 0.32);
  --cnm-input-text: #f1f5f9;
  --cnm-primary: #93caff;
  --cnm-primary-soft: rgba(147, 202, 255, 0.22);
}

.cn-modal-dialog .cn-modal-form .cn-modal-textarea,
.cn-modal-dialog .cn-modal-form .form-control,
.dark-layout .cn-modal-dialog .cn-modal-form .cn-modal-textarea,
.dark-layout .cn-modal-dialog .cn-modal-form .form-control {
  border: 1px solid var(--cnm-input-border);
  border-radius: 8px;
  padding: 12px 14px;
  font-size: 0.95rem;
  line-height: 1.55;
  resize: vertical;
  min-height: 120px;
  box-shadow: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
  background-color: var(--cnm-input-bg);
  color: var(--cnm-input-text);
}
.cn-modal-dialog .cn-modal-form .cn-modal-textarea:hover,
.dark-layout .cn-modal-dialog .cn-modal-form .cn-modal-textarea:hover {
  border-color: var(--cnm-input-border-strong);
}
.cn-modal-dialog .cn-modal-form .cn-modal-textarea:focus,
.cn-modal-dialog .cn-modal-form .form-control:focus,
.dark-layout .cn-modal-dialog .cn-modal-form .cn-modal-textarea:focus,
.dark-layout .cn-modal-dialog .cn-modal-form .form-control:focus {
  outline: none;
  border-color: var(--cnm-primary);
  box-shadow: 0 0 0 3px var(--cnm-primary-soft);
  background-color: var(--cnm-input-bg);
}
.cn-modal-dialog .cn-modal-form .cn-modal-textarea::placeholder {
  color: var(--cnm-input-border-strong);
  opacity: 1;
}

.cn-modal-flags-row {
  display: flex;
  gap: 8px;
  margin-top: 14px;
  flex-wrap: wrap;
}

.cn-flag-toggle {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 14px;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 9px;
  font-size: 0.84rem;
  font-weight: 600;
  color: #475569;
  cursor: pointer;
  transition: all 0.15s ease;
}
.cn-flag-toggle:hover {
  background: #eff6ff;
  border-color: #2563eb;
  color: #2563eb;
}
.cn-flag-toggle.on {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.3);
}
.cn-flag-toggle.cn-flag-important.on {
  background: #f59e0b;
  border-color: #f59e0b;
  color: #fff;
  box-shadow: 0 2px 6px rgba(245, 158, 11, 0.3);
}
.cn-flag-toggle.cn-flag-pin.on {
  background: #2563eb;
  border-color: #2563eb;
  color: #fff;
}
.dark-layout .cn-flag-toggle {
  background: #2a2f3a;
  border-color: #475569;
  color: #cbd5e1;
}
.dark-layout .cn-flag-toggle:hover {
  background: rgba(96, 165, 250, 0.12);
  color: #60a5fa;
  border-color: #60a5fa;
}

.cn-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}
.dark-layout .cn-modal-footer { border-top-color: #475569; }

.cn-modal-cancel {
  color: #475569;
  font-weight: 500;
  text-decoration: none;
}
.cn-modal-cancel:hover { color: #0f172a; }
.dark-layout .cn-modal-cancel { color: #94a3b8; }
.dark-layout .cn-modal-cancel:hover { color: #f1f5f9; }

.cn-modal-submit {
  border-radius: 9px;
  padding: 8px 22px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
}

/* ====== DARK MODE ====== */
.dark-layout .cn-title {
  color: #e2e6ee;
}

.dark-layout .cn-toolbar {
  border-bottom-color: #3d4452;
}

.dark-layout .cn-search >>> .input-group-text {
  background: #1f242e;
  border-color: #3d4452;
  color: #98a0b3;
}

.dark-layout .cn-search >>> .form-control {
  background: #2a2f3a;
  border-color: #3d4452;
  color: #e2e6ee;
}

.dark-layout .cn-chip {
  background: #2a2f3a;
  border-color: #3d4452;
  color: #b8c0d0;
}

.dark-layout .cn-chip:hover {
  background: rgba(0, 123, 255, 0.18);
  color: #6ab0ff;
  border-color: #4d80b8;
}

.dark-layout .cn-note {
  background: #2a2f3a;
  border-color: #3d4452;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.25);
}

.dark-layout .cn-note:hover {
  border-color: #4d556a;
}

.dark-layout .cn-note.pinned {
  background: linear-gradient(120deg, rgba(0, 123, 255, 0.14) 0%, #2a2f3a 60%);
}

.dark-layout .cn-note.important {
  background: linear-gradient(120deg, rgba(248, 196, 0, 0.12) 0%, #2a2f3a 60%);
}

.dark-layout .cn-author-chip {
  background: #1f242e;
  border-color: #3d4452;
  color: #e2e6ee;
}

.dark-layout .cn-author-chip-mine {
  background: rgba(0, 123, 255, 0.18);
  border-color: #4d80b8;
}

.dark-layout .cn-time,
.dark-layout .cn-note-meta {
  color: #98a0b3;
}

.dark-layout .cn-flag-public {
  background: rgba(31, 122, 58, 0.2);
  color: #6dd29a;
}

.dark-layout .cn-flag-private {
  background: rgba(178, 43, 58, 0.2);
  color: #ff8a95;
}

.dark-layout .cn-icon-btn {
  background: #1f242e;
  border-color: #3d4452;
  color: #b8c0d0;
}

.dark-layout .cn-icon-btn:hover {
  background: rgba(0, 123, 255, 0.18);
  color: #6ab0ff;
  border-color: #4d80b8;
}

.dark-layout .cn-note-body {
  color: #e2e6ee;
}

.dark-layout .cn-note-tags {
  border-top-color: #3d4452;
}

.dark-layout .cn-tag-important {
  color: #4a3500;
}

.dark-layout .cn-empty {
  background: #1f242e;
  border-color: #3d4452;
  color: #b8c0d0;
}

.dark-layout .cn-empty-icon {
  color: #4d556a;
}
</style>
